import {useState} from 'react';
import {useUrl} from '@shopify/hydrogen';
import clsx from 'clsx';
import {Fragment} from 'react';
import PropTypes from 'prop-types';

import {ToastContainer} from 'react-toastify';
import {useSubdomainBrand} from '~/hooks/useSubdomainBrand';
import {sanityLinkToAnchor} from '~/utils/link';

import {SearchForm} from '~/components/search/SearchForm.client';
// import {Banner} from '~/components/site-top-bar/Banner.client';
import 'react-toastify/dist/ReactToastify.css';
import {ReactComponent as SearchIcon} from '~/assets/icons/search.svg';
import {ReactComponent as NavBarsIcon} from '~/assets/icons/nav-bars.svg';
import {ReactComponent as ChevronDownIcon} from '~/assets/icons/chevron-down.svg';
import {ReactComponent as CloseIcon} from '~/assets/icons/close.svg';
import {UserNav} from '~/components/site-top-bar/UserNav.client';
import {trackClickEvent} from '~/utils/trackClickEvent';

/**
 *  PrimaryNav component
 *  Generates a navigational map of pages sorted into logical categories from Sanity data,
 *  and properly associates those menu items with the correct links
 */

export function PrimaryNav({sanityData}) {
  const {generalSiteSettings, headerMenu} = sanityData ?? {};

  const [searchIsVisible, setSearchIsVisible] = useState(false);
  const [primaryMenuIsVisible, setPrimaryMenuIsVisible] = useState(false);

  function toggleSearchVisibility() {
    if (!searchIsVisible) {
      trackClickEvent('search_initiate');
    }
    setSearchIsVisible((searchIsVisible) => !searchIsVisible);
    setPrimaryMenuIsVisible(false);
  }

  function togglePrimaryMenuVisibility() {
    setPrimaryMenuIsVisible((primaryMenuIsVisible) => !primaryMenuIsVisible);
    setSearchIsVisible(false);
  }

  return (
    <>
      {/* NOTE: picking here for the toast container to live, just due to being a global,
          top-level client component - wrapped ToastContainer in div to remove unexpected HTML warning */}
      <div>
        <ToastContainer style={{zIndex: 9001}} />
      </div>
      <div className={clsx(`primary-nav`, primaryMenuIsVisible && 'active')}>
        {/* <Banner header="Exclusive rewards for our members! Sign up today.">
        Rewards coming soon </Banner> */}

        <div className={`primary-nav__inner container`}>
          <div className="mobile-nav__header">
            <a
              href="/"
              className={`primary-nav__logo`}
              onClick={() => {
                trackClickEvent('logo_click');
              }}
            >
              <img
                src={generalSiteSettings?.logoDesktop?.asset?.url ?? ''}
                alt="The primary, featured brand logo"
              />
            </a>
            <button
              className={clsx(
                'mobile-nav__trigger mobile-nav__trigger-hamburger',
                'strokesvg',
                !primaryMenuIsVisible && 'active',
              )}
              onClick={() => {
                togglePrimaryMenuVisibility();
                trackClickEvent('toggle_primary_menu_visibility');
              }}
            >
              <NavBarsIcon />
            </button>
            <div
              className={clsx(
                'mobile-nav__trigger-search',
                !primaryMenuIsVisible && 'active',
              )}
            >
              <Search
                toggleSearchVisibility={toggleSearchVisibility}
                searchIsVisible={searchIsVisible}
              />
            </div>
            <div className="mobile-nav__trigger-usernav active">
              <UserNav />
            </div>
            <button
              className={clsx(
                'mobile-nav__trigger mobile-nav__trigger-close',
                primaryMenuIsVisible && 'active',
              )}
              onClick={() => {
                togglePrimaryMenuVisibility();
                trackClickEvent('toggle_primary_menu_visibility');
              }}
            >
              <CloseIcon />
            </button>
            <ul
              className={clsx(
                `primary-nav__top-level-menu`,
                primaryMenuIsVisible && 'active',
              )}
            >
              <PrimaryMenuItems headerMenu={headerMenu ?? []} />
            </ul>
          </div>
        </div>
      </div>
    </>
  );
}

function PrimaryMenuItems({headerMenu}) {
  const {href} = useUrl();
  const brand = useSubdomainBrand();

  const [visibleSubmenu, setVisibleSubmenu] = useState('');

  function toggleSubmenuVisibility(menuItem) {
    setVisibleSubmenu((prev) =>
      prev !== menuItem.title ? menuItem.title : '',
    );
  }

  return (
    <>
      {headerMenu.map((menuItem, brand) => {
        // TODO: figure out href matching and/or active state, now that we are wired
        // to Sanity
        const active = visibleSubmenu === menuItem.title;

        const optionalLink = sanityLinkToAnchor(
          menuItem.navigationOptions?.[0],
          brand,
        );

        return (
          <Fragment key={menuItem._key}>
            <li
              id={menuItem.title?.replace(/\s/g, '_')}
              className={clsx(
                `primary-nav__list-item`,
                optionalLink?.href &&
                  href.includes(optionalLink.href) &&
                  'active',
              )}
            >
              <div
                className={clsx(
                  `primary-nav__list-item-inner`,
                  active && 'active',
                )}
              >
                {optionalLink?.href ? (
                  <h5>
                    <a {...optionalLink}>{menuItem.title ?? ''}</a>
                  </h5>
                ) : (
                  <h5>{menuItem.title ?? ''}</h5>
                )}
                {menuItem.sections && menuItem.sections.length > 0 && (
                  <button
                    className="subnav-trigger"
                    onClick={() => {
                      toggleSubmenuVisibility(menuItem);
                      trackClickEvent('toggle_submenu_visibility_toggle');
                    }}
                  >
                    <ChevronDownIcon />
                  </button>
                )}
              </div>
              {menuItem.sections && menuItem.sections.length > 0 && (
                <div
                  className={clsx(
                    `primary-nav__lower-level-menu submenu`,
                    active && 'active',
                  )}
                >
                  <Submenu sections={menuItem.sections} />
                </div>
              )}
            </li>
          </Fragment>
        );
      })}
    </>
  );
}

/* populate the dropdown menus for each of the top-level menu nav items within "submenu" */
function Submenu({sections, brand}) {
  return (
    <div className="submenu__inner container">
      {sections.map((submenu) => {
        const {_key, links, navigationOptions, title} = submenu;

        return (
          <div className="submenu__unit" key={_key}>
            <a
              // If `navigationOptions` exists, it is only going to be of length 1,
              // and represents a top-level link for this submenu section ...
              {...sanityLinkToAnchor(navigationOptions?.[0], brand)}
              className="submenu__header"
            >
              {title ?? ''}
            </a>
            <ul className="submenu__list">
              {/* populate the list of links within each submenu */}
              {links?.map((link) => (
                <li key={link._key} className="submenu__list-item">
                  {/* ... and expect a similar child structure here: */}
                  <a
                    {...sanityLinkToAnchor(link.navigationOptions?.[0], brand)}
                    className="submenu__link"
                  >
                    {link.title ?? ''}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

function Search({searchIsVisible, toggleSearchVisibility}) {
  return (
    <div className={`primary-nav__search-form-wrapper`}>
      <button
        className="searchbar-trigger"
        onClick={() => {
          toggleSearchVisibility();
          trackClickEvent('toggle_search_visibility');
        }}
      >
        <SearchIcon />
      </button>
      <div
        className={clsx(
          `primary-nav__search-form-container search-form-container`,
          searchIsVisible && 'active',
        )}
      >
        <SearchForm
          placeholderText="What are you looking for?"
          formId="search-form"
          hasIconInPlaceholder={true}
          focusDep={searchIsVisible}
        />
      </div>
    </div>
  );
}

PrimaryNav.PropTypes = {
  sanityData: PropTypes.object.isRequired,
};
